import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { format, parseISO, subDays } from 'date-fns';
import { DocumentChartBarIcon } from '@heroicons/react/24/outline';
import ReportTemplate from '../../components/reports/ReportTemplate';
import DateRangePicker from '../../components/reports/DateRangePicker';
import CSVExportButton from '../../components/reports/CSVExportButton';
import { ROUTES } from '../../consts/general';
import { FEDEX_BILLING_RECONCILIATION } from '../../consts/reports';
import { useAbortableRequest } from '../../helpers/useAbortableRequest';
import { useInitialMountDelay } from '../../helpers/useInitialMountDelay';
import useDateRangeParams from '../../hooks/useDateRangeParams';

const PAGE_SIZE = 50;
const STATUSES = [
  'shipped_not_billed',
  'billed_not_in_shiphero',
  'matched',
  'surcharge',
  'box_upcharge',
  'cost_mismatch',
  'under_billed',
];

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });
const formatValue = (value, column) => {
  if (value === null || value === undefined || value === '') return '—';
  if (column.type === 'currency') return Number(value).toLocaleString('en-US', { style: 'currency', currency: 'USD' });
  if (column.type === 'date') {
    const parsed = parseISO(String(value).slice(0, 10));
    return Number.isNaN(parsed.getTime()) ? value : format(parsed, 'MM/dd/yyyy');
  }
  return value;
};

const FedexBillingReconciliationReport = ({ marginFromSidebar }) => {
  const navigate = useNavigate();
  const fileInput = useRef(null);
  const { beginRequest, isLatest } = useAbortableRequest();
  const initialReady = useInitialMountDelay(500);
  const defaultEnd = format(new Date(), 'yyyy-MM-dd');
  const defaultStart = format(subDays(new Date(), 59), 'yyyy-MM-dd');
  const { startDate, endDate, setDateRange } = useDateRangeParams(defaultStart, defaultEnd);
  const [status, setStatus] = useState('all');
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [data, setData] = useState([]);
  const [columns, setColumns] = useState([]);
  const [totals, setTotals] = useState({});
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [uploadMessage, setUploadMessage] = useState(null);

  const params = useCallback((includePage = true) => {
    const query = new URLSearchParams({ startDate, endDate });
    if (includePage) {
      query.set('page', page);
      query.set('pageSize', PAGE_SIZE);
    }
    if (status !== 'all') query.set('status', status);
    if (invoiceNumber) query.set('invoiceNumber', invoiceNumber);
    return query;
  }, [startDate, endDate, page, status, invoiceNumber]);

  const load = useCallback(async () => {
    const { signal, requestId } = beginRequest();
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`/api/v1/reports/${FEDEX_BILLING_RECONCILIATION}?${params()}`, {
        headers: authHeaders(),
        signal,
      });
      const json = await response.json();
      if (!response.ok) throw new Error(json.error || 'Failed to load report');
      if (!isLatest(requestId)) return;
      setData(json.data || []);
      setColumns(json.columns || []);
      setTotals(json.totals || {});
      setTotalRecords(json.totalRecords || 0);
      setTotalPages(json.totalPages || 1);
    } catch (requestError) {
      if (requestError?.name === 'AbortError' || !isLatest(requestId)) return;
      setError(requestError.message);
      setData([]);
    } finally {
      if (isLatest(requestId)) setLoading(false);
    }
  }, [beginRequest, isLatest, params]);

  useEffect(() => {
    if (initialReady) load();
  }, [initialReady, load]);

  useEffect(() => setPage(1), [startDate, endDate, status, invoiceNumber]);

  const uploadInvoice = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    const formData = new FormData();
    formData.append('file', file);
    try {
      const response = await fetch('/api/v1/carrier-invoices/upload', {
        method: 'POST',
        headers: authHeaders(),
        body: formData,
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.error || 'Invoice upload failed');
      setUploadMessage(result.status === 'duplicate'
        ? `Invoice ${result.invoice?.invoice_number || ''} is already imported.`
        : `Imported invoice ${result.invoice?.invoice_number || ''}: ${result.invoice?.shipment_count || result.lineCount || 0} shipments`);
      load();
    } catch (uploadError) {
      setUploadMessage(uploadError.message);
    }
  };

  const exportData = async () => {
    const response = await fetch(`/api/v1/reports/${FEDEX_BILLING_RECONCILIATION}/export?${params(false)}`, { headers: authHeaders() });
    if (!response.ok) throw new Error('Failed to export report');
    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'fedex-billing-reconciliation.csv';
    link.click();
    URL.revokeObjectURL(url);
    return { handled: true };
  };

  const filters = (
    <div className="contents">
      <DateRangePicker
        startDate={startDate}
        endDate={endDate}
        onDateChange={({ startDate: start, endDate: end }) => {
          setDateRange({ startDate: start, endDate: end });
          setPage(1);
        }}
        label="FedEx ship date range"
      />
      <select className="rounded border px-3 py-2 text-sm" value={status} onChange={(event) => setStatus(event.target.value)}>
        <option value="all">All statuses</option>
        {STATUSES.map((value) => <option key={value} value={value}>{value}</option>)}
      </select>
      <input
        className="rounded border px-3 py-2 text-sm"
        placeholder="Invoice number"
        value={invoiceNumber}
        onChange={(event) => setInvoiceNumber(event.target.value)}
      />
      <button type="button" className="rounded bg-blue-600 px-3 py-2 text-sm text-white" onClick={() => fileInput.current?.click()}>
        Upload FedEx invoice PDF
      </button>
      <input ref={fileInput} className="hidden" type="file" accept="application/pdf,.pdf" onChange={uploadInvoice} />
    </div>
  );

  const pagination = (
    <div className="flex items-center justify-between text-sm">
      <span>{totalRecords.toLocaleString()} rows</span>
      <div className="flex gap-2">
        <button type="button" disabled={page <= 1} onClick={() => setPage((value) => value - 1)} className="rounded border px-3 py-1 disabled:opacity-50">Previous</button>
        <span className="py-1">Page {page} of {totalPages}</span>
        <button type="button" disabled={page >= totalPages} onClick={() => setPage((value) => value + 1)} className="rounded border px-3 py-1 disabled:opacity-50">Next</button>
      </div>
    </div>
  );

  return (
    <ReportTemplate
      reportId={FEDEX_BILLING_RECONCILIATION}
      title="FedEx Billing Reconciliation"
      description="Compare FedEx invoice charges with ShipHero shipping-label costs."
      breadcrumbs={[{ label: 'Reports', icon: <DocumentChartBarIcon className="h-5 w-5 text-gray-400" /> }, { label: '' }]}
      onBack={() => navigate(ROUTES.REPORTS)}
      marginFromSidebar={marginFromSidebar}
      isLoading={loading}
      error={error}
      filters={filters}
      exportButton={<CSVExportButton data={data} columns={columns} filename="fedex-billing-reconciliation" onExport={exportData} />}
      pagination={pagination}
      renderChildrenOnly
    >
      <div className="space-y-3">
        {uploadMessage && <div className="rounded border bg-blue-50 px-4 py-2 text-sm">{uploadMessage}</div>}
        <div className="grid gap-3 rounded border bg-white p-4 md:grid-cols-4">
          {[
            ['count', 'Rows'], ['shiphero_label_cost', 'ShipHero label cost'],
            ['fedex_total', 'FedEx total'], ['overbilled_total', 'Overbilled total'],
          ].map(([key, label]) => (
            <div key={key} className="rounded bg-gray-50 p-3">
              <div className="text-xs text-gray-500">{label}</div>
              <div className="font-semibold">{formatValue(totals[key], { type: key === 'count' ? 'number' : 'currency' })}</div>
            </div>
          ))}
        </div>
        <div className="overflow-auto rounded border bg-white">
          {data.length === 0 ? <div className="px-6 py-12 text-center text-sm text-gray-500">No FedEx reconciliation rows in the selected range.</div> : (
            <table className="min-w-max text-left text-xs">
              <thead className="sticky top-0 bg-gray-100"><tr>{columns.map((column) => <th key={column.key} className="whitespace-nowrap px-3 py-2">{column.label}</th>)}</tr></thead>
              <tbody>{data.map((row, index) => <tr key={`${row.order_number || row.tracking_number}-${index}`} className={index % 2 ? 'bg-gray-50' : ''}>
                {columns.map((column) => <td key={column.key} className="whitespace-nowrap px-3 py-2">{formatValue(row[column.key], column)}</td>)}
              </tr>)}</tbody>
            </table>
          )}
        </div>
      </div>
    </ReportTemplate>
  );
};

export default FedexBillingReconciliationReport;
